import '../../styles/LandingPage/ResearchPillars/AssetLiability.css';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function AssetLiability() {
  const contentRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const items = contentRef.current?.querySelectorAll('.al-animate');
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section className="al-detail-section" ref={contentRef}>
      <div className="al-detail-container">
        <Link to="/" className="al-back-link al-animate">
          <i className="ti ti-arrow-left" />
          Back to Research Pillars
        </Link>

        <div className="al-detail-header al-animate">
          <div className="al-detail-label">Pillar 04</div>
          <div className="al-detail-icon">
            <i className="ti ti-scale" />
          </div>
          <h1>Asset-Liability & Investment Risk</h1>
          <p className="al-detail-subtitle">
            ALM modelling, duration mismatch analysis, and investment risk management for long-term African life portfolios.
          </p>
        </div>

        <div className="al-content">
          <div className="al-block al-animate">
            <h2>ALM Modelling Frameworks</h2>
            <p>
              Asset-liability management is critical for insurers with long-duration liabilities and limited matching assets. 
              Our research develops ALM frameworks that account for the structural constraints of African capital markets — 
              including shallow bond markets, limited long-dated instruments, and high sovereign risk concentrations. 
              We model the interaction between liability cash flows and asset portfolios under various reinvestment assumptions, 
              examining how cash flow matching, duration matching, and immunisation strategies perform in constrained markets.
            </p>
            <div className="al-highlight">
              <i className="ti ti-bulb" />
              <span>Key Insight: The average duration gap in African life portfolios exceeds 8 years, compared to 2-3 years in developed markets.</span>
            </div>
          </div>

          <div className="al-block al-animate">
            <h2>Duration Mismatch Analysis</h2>
            <p>
              Duration mismatch creates exposure to interest rate risk that can significantly impact solvency and profitability. 
              Our research quantifies the economic cost of duration gaps under various interest rate scenarios, 
              including the asymmetric impact of rising versus falling rates. We examine how regulatory accounting 
              frameworks — particularly IFRS 17 and local statutory requirements — create different incentives 
              for duration management, and how insurers can optimise their asset allocation within these constraints.
            </p>
          </div>

          <div className="al-block al-animate">
            <h2>Inflation Risk in Long-Term Products</h2>
            <p>
              Inflation poses a unique threat to long-term life products with fixed benefit guarantees. Our research 
              models the impact of inflation on real policyholder returns, examining how index-linked products, 
              participating policies, and discretionary bonuses can mitigate inflation risk. We analyse the 
              correlation between inflation and other macroeconomic variables — including interest rates, equity returns, 
              and currency values — to develop integrated risk models that capture the full inflation exposure of insurance portfolios.
            </p>
          </div>

          <div className="al-block al-animate">
            <h2>Scenario Generation & Stochastic Modelling</h2>
            <p>
              Realistic scenario generation is essential for ALM and economic capital calculations. Our research develops 
              stochastic models for African asset classes that capture their unique characteristics — including high volatility, 
              fat tails, and regime-switching behaviour. We examine the appropriateness of standard models such as 
              geometric Brownian motion, mean-reverting processes, and copula-based multivariate models for African markets, 
              and develop calibration techniques using limited historical data.
            </p>
          </div>

          <div className="al-block al-animate">
            <h2>Current Research Outputs</h2>
            <div className="al-outputs">
              <div className="al-output-card">
                <i className="ti ti-file-analytics" />
                <h4>African ALM Best Practice Guide</h4>
                <span>Technical Paper · 2025</span>
              </div>
              <div className="al-output-card">
                <i className="ti ti-chart-area" />
                <h4>Duration Gap Monitoring Dashboard</h4>
                <span>Model & Documentation · 2025</span>
              </div>
              <div className="al-output-card">
                <i className="ti ti-refresh" />
                <h4>Stochastic Scenario Generator</h4>
                <span>Open-source Tool · 2025</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}